import React from "react";
import { ScanLine, Clock, FileText, Cloud, ShieldCheck, ArrowUpRight, MapPin, Mic, Receipt, Users, UsersRound, Camera, Paperclip, Image as ImageIcon, Timer, ListChecks, ClipboardCheck, Zap, Send, WifiOff, Landmark } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { PhoneShot } from "./PhoneShot";

export const Features = () => {
  const { t } = useLanguage();
  const CARDS = [
    { id: "ai-laheteluku", icon: ScanLine, k: "ai", span: "md:col-span-4", accent: true },
    { id: "tuntikirjaukset", icon: Clock, k: "time", span: "md:col-span-4", image: "/assets/img-liukukello.webp" },
    { id: "pdf-raportit", icon: FileText, k: "pdf", span: "md:col-span-4" },
    { id: "pilviturva", icon: Cloud, k: "cloud", span: "md:col-span-4", accent: true },
    { id: "ai-puhesanelu", icon: Mic, k: "voice", span: "md:col-span-4" },
    { id: "laskutus", icon: Receipt, k: "billing", span: "md:col-span-4", accent: true },
  ];
  const V240_CARDS = [
    { id: "digitaalinen-leimasin", icon: MapPin, k: "leimasin", image: "/assets/img-gps.png" },
    { id: "pomo-vision", icon: Camera, k: "pomovision", image: "/assets/img-team.png" },
    { id: "tuplapomo", icon: UsersRound, k: "tuplapomo", image: "/assets/img-tuplapomo.webp" },
    { id: "liitetiedostot", icon: Paperclip, k: "attachments", image: "/assets/img-attachments.png" },
    { id: "pomo-projekti", icon: Users, k: "pomo_projekti", image: "/assets/img-pomo-projekti.webp" },
    { id: "seuranta", icon: ListChecks, k: "seuranta", image: "/assets/img-seuranta.jpeg" },
    { id: "poytakirja", icon: ClipboardCheck, k: "poytakirja", image: "/assets/img-poytakirja.jpeg" },
    { id: "visma", icon: Zap, k: "visma", image: "/assets/img-visma.jpeg", badge: "beta" },
    { id: "netvisor", icon: Send, k: "netvisor", image: "/assets/img-netvisor.jpeg", badge: "beta" },
    { id: "easor", icon: Landmark, k: "easor", badge: "coming", span: "md:col-span-2 lg:col-span-3", placeholderIcon: Landmark },
    { id: "offline", icon: WifiOff, k: "offline", span: "md:col-span-2 lg:col-span-3" },
  ];

  return (
    <section id="ominaisuudet" data-testid="features-section" className="relative py-24 md:py-32 border-t-2 border-[#1a1a1a] grain">
      <div className="absolute top-8 left-5 md:left-10 font-mono text-[10px] tracking-[0.3em] text-[#A3A3A3] uppercase">{t("features.section_number")}</div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-14">
          <div className="md:col-span-7">
            <div className="font-mono text-[11px] tracking-[0.3em] text-[#FFEA00] uppercase mb-4">{t("features.eyebrow")}</div>
            <h2 data-testid="features-heading" className="font-display font-bold text-white text-4xl md:text-6xl lg:text-7xl uppercase leading-none tracking-tight">
              {t("features.heading_line1")}
              <br />
              <span className="text-[#FFEA00]">{t("features.heading_line2_accent")}</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <div className="border-l-2 border-[#FFEA00] pl-4 py-2">
              <p className="text-[#A3A3A3] text-base leading-relaxed">{t("features.tagline")}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {CARDS.map((c) => (
            <FeatureCard key={c.id} id={c.id} Icon={c.icon}
              kicker={t(`features.cards.${c.k}.kicker`)}
              title={t(`features.cards.${c.k}.title`)}
              description={t(`features.cards.${c.k}.description`)}
              span={c.span} accent={c.accent} image={c.image} imageAlt={t(`features.cards.${c.k}.title`)} />
          ))}
        </div>

        {/* v2.4.0 section — active features shipped in the latest version */}
        <div data-testid="v240-section" className="mt-16 pt-10 border-t-2 border-dashed border-[#1f1f1f]">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
            <div>
              <div className="font-mono text-[11px] tracking-[0.3em] text-[#FFEA00] uppercase mb-3">{t("features.v240.eyebrow")}</div>
              <h3 className="font-display font-bold text-white text-3xl md:text-5xl uppercase leading-none tracking-tight">
                {t("features.v240.heading_line1")}{" "}
                <span className="text-[#FFEA00]">{t("features.v240.heading_line2_accent")}</span>
              </h3>
            </div>
            <span className="self-start inline-flex items-center gap-2 px-3 py-1.5 bg-[#FFEA00] text-black font-display font-bold text-xs tracking-[0.25em] uppercase shadow-[3px_3px_0_0_rgba(0,0,0,0.6)]">
              <span className="w-1.5 h-1.5 bg-black rounded-full pulse-dot" />
              {t("features.v240.badge_new")}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {V240_CARDS.map((c) => (
              <V240Card key={c.id} id={c.id} Icon={c.icon}
                kicker={t(`features.v240.cards.${c.k}.kicker`)}
                title={t(`features.v240.cards.${c.k}.title`)}
                description={t(`features.v240.cards.${c.k}.description`)}
                badgeLabel={t(`features.v240.badge_${c.badge || "new"}`)}
                badge={c.badge || "new"}
                image={c.image}
                placeholderIcon={c.placeholderIcon}
                imageAlt={t(`features.v240.cards.${c.k}.title`)}
                span={c.span}
              />
            ))}
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 max-w-md mx-auto">
          <RoadmapCard id="firman-logo" icon={ImageIcon} k="custom_logo" />
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({ id, Icon, title, kicker, description, span, accent, image, imageAlt }) => (
  <article data-testid={`feature-card-${id}`}
    className={`${span} group relative border-2 p-7 md:p-9 bg-[#0F0F0F] hover:bg-[#141414] transition-colors min-h-[260px] flex flex-col justify-between ${accent ? "border-[#FFEA00]/40 hover:border-[#FFEA00]" : "border-[#222] hover:border-[#FFEA00]"}`}>
    <div className="flex items-start justify-between">
      <div className={`w-14 h-14 flex items-center justify-center border-2 ${accent ? "border-[#FFEA00] bg-[#FFEA00]" : "border-[#FFEA00] bg-transparent"}`}>
        <Icon className={`w-7 h-7 stroke-[2] ${accent ? "text-black" : "text-[#FFEA00]"}`} />
      </div>
      <div className="font-mono text-[10px] tracking-[0.25em] text-[#A3A3A3] uppercase">{kicker}</div>
    </div>
    <div className="mt-10">
      <h3 className="font-display font-bold text-white text-3xl md:text-4xl uppercase leading-none tracking-tight">{title}</h3>
      <p className="mt-4 text-[#A3A3A3] text-sm md:text-base leading-relaxed max-w-md">{description}</p>
    </div>
    {image && (
      <div className="mt-6">
        <PhoneShot src={image} alt={imageAlt} testId={`feature-shot-${id}`} focus="top" aspect="portrait" zoomable />
      </div>
    )}
    <div className="absolute bottom-4 right-4 w-9 h-9 flex items-center justify-center text-[#333] group-hover:text-[#FFEA00] group-hover:bg-[#FFEA00]/10 transition-colors">
      <ArrowUpRight className="w-5 h-5 stroke-[2]" />
    </div>
    {accent && <div className="absolute top-0 right-0 w-2 h-2 bg-[#FFEA00]" />}
  </article>
);

const V240Card = ({ id, Icon, title, kicker, description, badgeLabel, badge, image, placeholderIcon, imageAlt, span }) => (
  <article data-testid={`v240-card-${id}`}
    className={`group relative border-2 border-[#FFEA00] bg-[#0F0F0F] hover:bg-[#141414] transition-colors min-h-[240px] p-6 md:p-7 flex flex-col justify-between shadow-[6px_6px_0_0_rgba(255,234,0,0.12)] ${span || ""}`}>
    <div className={`absolute -top-3 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 font-display font-bold text-[10px] tracking-[0.3em] uppercase ${badge === "coming" ? "border-2 border-[#FFEA00] bg-black text-[#FFEA00]" : "bg-[#FFEA00] text-black"}`}>
      {badgeLabel}
    </div>
    <div className="flex items-start justify-between">
      <div className="w-12 h-12 flex items-center justify-center border-2 border-[#FFEA00] bg-[#FFEA00]">
        <Icon className="w-6 h-6 stroke-[2] text-black" />
      </div>
      <div className="font-mono text-[10px] tracking-[0.25em] text-[#A3A3A3] uppercase text-right">{kicker}</div>
    </div>
    <div className="mt-6">
      <h4 className="font-display font-bold text-white text-2xl md:text-3xl uppercase leading-none tracking-tight">{title}</h4>
      <p className="mt-3 text-[#A3A3A3] text-sm leading-relaxed">{description}</p>
    </div>
    {(image || placeholderIcon) && (
      <div className={`mt-5 ${span ? "max-w-xs" : ""}`}>
        <PhoneShot
          src={image}
          Icon={placeholderIcon}
          iconLabel={placeholderIcon ? title : undefined}
          alt={imageAlt}
          testId={`v240-shot-${id}`}
          focus="top"
          aspect="portrait"
          zoomable
        />
      </div>
    )}
    <div className="absolute top-0 right-0 w-2 h-2 bg-[#FFEA00]" />
  </article>
);

const RoadmapCard = ({ id, icon: Icon, k, live = false, included = null, etaKey = "eta_q4" }) => {
  const { t } = useLanguage();
  const status = live ? t("features.roadmap.status_nyt_mukana") : t("features.roadmap.status_tulossa");
  const tag = live ? t("features.roadmap.tag_kaytossa") : t("features.roadmap.tag_roadmap");
  const eta = t(`features.roadmap.${etaKey}`);
  const includedLabel = included ? t(included.labelKey) : null;

  const handleIncludedClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const el = document.getElementById(included.elementId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div data-testid={`coming-soon-${id}`}
      className={`relative border-2 bg-[#0a0a0a] overflow-hidden group transition-colors ${live ? "border-[#FFEA00] hover:border-white" : "border-[#FFEA00]/40 hover:border-[#FFEA00]"}`}>
      <div className="absolute left-0 top-0 bottom-0 w-2 hazard-stripes-anim" />
      <div className="absolute -top-px -right-px w-3 h-3 bg-[#FFEA00]" />
      {!live && (
        <span
          data-testid={`roadmap-eta-${id}`}
          className="absolute top-3 right-4 z-10 inline-block px-2 py-0.5 border-2 border-[#FFEA00]/60 bg-black/70 font-mono text-[10px] tracking-[0.2em] text-[#FFEA00] uppercase"
        >
          {eta}
        </span>
      )}
      <div className="relative pl-8 pr-6 py-6 flex items-start gap-5">
        <div className={`shrink-0 w-14 h-14 border-2 flex items-center justify-center ${live ? "border-[#FFEA00] bg-[#FFEA00]" : "border-[#FFEA00] bg-[#FFEA00]/[0.06]"}`}>
          <Icon className={`w-7 h-7 stroke-[2] ${live ? "text-black" : "text-[#FFEA00]"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#FFEA00] text-black font-display font-bold text-[10px] tracking-[0.25em] uppercase">
              <span className="w-1.5 h-1.5 bg-black rounded-full pulse-dot" />
              {status}
            </span>
            <span className="font-mono text-[10px] tracking-[0.25em] text-[#A3A3A3] uppercase">{tag}</span>
          </div>
          <h3 className="font-display font-bold text-white text-xl md:text-2xl uppercase tracking-tight leading-tight">{t(`features.roadmap.${k}.title`)}</h3>
          <p className="mt-1.5 text-sm md:text-base text-[#A3A3A3] leading-relaxed">{t(`features.roadmap.${k}.description`)}</p>
          {included && (
            <button
              type="button"
              onClick={handleIncludedClick}
              data-testid={`roadmap-included-${id}`}
              className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 border-2 border-[#FFEA00]/70 bg-transparent hover:bg-[#FFEA00] hover:text-black text-[#FFEA00] transition-colors"
            >
              <span className="font-display font-bold text-xs tracking-[0.15em] uppercase">
                {includedLabel}
              </span>
              <ArrowUpRight className="w-3.5 h-3.5 stroke-[2.5]" />
            </button>
          )}
        </div>
        <div className="hidden md:flex shrink-0 w-9 h-9 items-center justify-center text-[#333] group-hover:text-[#FFEA00] transition-colors">
          <ArrowUpRight className="w-5 h-5 stroke-[2]" />
        </div>
      </div>
    </div>
  );
};
